<script setup lang="ts">
import { Button } from '@/components/ui/button';
import RangeCalendarEditor from '@/ui-plus/date-time-prop-editor/RangeCalendarEditor.vue';
import {
    type DateRangeValueType,
} from '@lib/zod/dateTimePropEditor';
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { toast } from '@/ui-plus/sonner';
import { onMounted, ref, watch } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import PartnerCurrencyReportSection from '@partner-spa-vue/components/PartnerCurrencyReportSection.vue';

const props = defineProps<{
    partnerType: 'marketing' | 'support';
}>();

type ReportsData = ResType<typeof dashboardApiClient.website.partner_marketing.store_reports[':websiteId']['$post']>['data'];

const reports = ref<ReportsData['reports']>([]);
const isLoading = ref(false);
const dateLabel = ref('Last 30 days');
const isCustomDateRange = ref(false);
const chartGranularity = ref<'daily' | 'weekly'>('daily');

const today = new Date();
const defaultStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
defaultStart.setHours(0, 0, 0, 0);
const defaultEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
defaultEnd.setHours(23, 59, 59, 999);

const dateRange = ref<DateRangeValueType>({
    start: defaultStart.getTime(),
    end: defaultEnd.getTime(),
});

const loadData = async () => {
    isLoading.value = true;

    const websiteId = currentWebsiteId.value || '';
    const json = {
        start: dateRange.value.start,
        end: dateRange.value.end,
    };

    const res = props.partnerType === 'marketing'
        ? await (await dashboardApiClient.website.partner_marketing.store_reports[':websiteId'].$post({
            param: { websiteId },
            json,
        })).json()
        : await (await dashboardApiClient.website.partner_support.store_reports[':websiteId'].$post({
            param: { websiteId },
            json,
        })).json();

    isLoading.value = false;

    if (res.error) {
        toast.error('Failed to load reports');
        console.error(res.error);
        return;
    }
    if (res.data) {
        reports.value = res.data.reports;
    }
};

const setDateRange = (days: number) => {
    if (days === 0) dateLabel.value = 'Today';
    else if (days === 90) dateLabel.value = 'Last 3 months';
    else dateLabel.value = `Last ${days} days`;

    isCustomDateRange.value = false;
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    endDate.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startDate.getTime(),
        end: endDate.getTime(),
    };
};

const setThisWeek = () => {
    dateLabel.value = 'This week';
    isCustomDateRange.value = false;
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startOfWeek.getTime(),
        end: endOfWeek.getTime(),
    };
};

const setThisMonth = () => {
    dateLabel.value = 'This month';
    isCustomDateRange.value = false;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startOfMonth.getTime(),
        end: endOfMonth.getTime(),
    };
};

const getFormattedRange = () => {
    return `${new Date(dateRange.value.start ?? 0).toLocaleDateString('en-CA')} - ${new Date(dateRange.value.end ?? 0).toLocaleDateString('en-CA')}`;
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(dateRange, () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        loadData();
    }, 400);
}, { deep: true });

onMounted(() => {
    loadData();
});
</script>

<template>
    <div class="space-y-6">
        <div class="text-sm text-muted-foreground space-y-1">
            <p>Paid totals exclude unpaid amounts. Unpaid is shown separately.</p>
            <p>Today’s earnings may still be updating.</p>
            <p>Stripe payments that aren’t captured yet usually appear within 4–6 hours.</p>
        </div>
        <DropdownMenu>
            <DropdownMenuTrigger as-child>
                <Button variant="outline"
                        size="sm">
                    {{ isCustomDateRange ? 'Custom date' : dateLabel }}
                    {{ getFormattedRange() }}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuItem @click="setDateRange(0)">
                    Today
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(7)">
                    Last 7 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(30)">
                    Last 30 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(90)">
                    Last 3 months
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem @click="setThisWeek">
                    This week
                </DropdownMenuItem>
                <DropdownMenuItem @click="setThisMonth">
                    This month
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div class="px-2 py-1.5">
                    <div class="flex items-center gap-2 mb-2">
                        <Switch :checked="isCustomDateRange"
                                @update:checked="isCustomDateRange = $event" />
                        Custom
                    </div>
                    <RangeCalendarEditor v-model="dateRange"
                                         v-if="isCustomDateRange" />
                </div>
            </DropdownMenuContent>
        </DropdownMenu>

        <Loader2 v-if="isLoading"
                 class="animate-spin h-8 w-8" />

        <template v-else>
            <p v-if="reports.length === 0"
               class="text-muted-foreground text-center text-sm py-8">
                No data in this range
            </p>
            <template v-else>
                <div class="flex items-center gap-2">
                    <Button size="sm"
                            :variant="chartGranularity === 'daily' ? 'default' : 'outline'"
                            @click="chartGranularity = 'daily'">
                        Daily
                    </Button>
                    <Button size="sm"
                            :variant="chartGranularity === 'weekly' ? 'default' : 'outline'"
                            @click="chartGranularity = 'weekly'">
                        Weekly
                    </Button>
                </div>

                <PartnerCurrencyReportSection v-for="report in reports"
                                              :key="report.currency"
                                              :report="report"
                                              :partner-type="partnerType"
                                              :date-range="dateRange"
                                              :granularity="chartGranularity" />
            </template>
        </template>
    </div>
</template>
